import { useCallback, useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { getOrganizationsSocket } from '../../../../infra/sockets/organization-socket';
import useSocket from '../../../../shared/hooks/use-socket';
import { BulkOperationsStatusPayload } from '../typings/bulk-action-progress';
import { bulkActionProgressAtom } from '../states/bulk-action-progress';
import { getBulkActionOperations } from '../requests/get-bulk-action-operations';
import { getCurrentUserId } from '../../../../infra/firebase/init';

const BULK_CONVERSATION_ACTION_TYPE = 'BULK_CONVERSATION_ACTION';

const useBulkActionSocket = () => {
  const { socket: organisationSocket } = useSocket(getOrganizationsSocket);
  const setBulkActionProgress = useSetAtom(bulkActionProgressAtom);

  useEffect(() => {
    getBulkActionOperations()
      .then((ops) => {
        if (ops.length > 0) setBulkActionProgress(ops);
      })
      .catch(() => {});
  }, [setBulkActionProgress]);

  const operationsStatusListener = useCallback(
    (payload: BulkOperationsStatusPayload) => {
      if (payload.type !== BULK_CONVERSATION_ACTION_TYPE) return;

      const currentUserId = getCurrentUserId();
      if (currentUserId && payload.validUserIds && !payload.validUserIds.includes(currentUserId)) return;

      const {
        operationId,
        status,
        additionalData: { action, total, succeeded, failed },
      } = payload;

      if (status === 'FAILED') {
        setBulkActionProgress((prev) => prev.filter((op) => op.operationId !== operationId));
        return;
      }

      setBulkActionProgress((prev) => {
        const newOp = { operationId, status, action, total, succeeded, failed };
        const idx = prev.findIndex((op) => op.operationId === operationId);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = newOp;
          return updated;
        }
        return [newOp, ...prev];
      });
    },
    [setBulkActionProgress]
  );

  useEffect(() => {
    organisationSocket.on('operations-status', operationsStatusListener);
    return () => {
      organisationSocket.off('operations-status', operationsStatusListener);
    };
  }, [operationsStatusListener, organisationSocket]);
};

export default useBulkActionSocket;
